import React, { useEffect } from 'react';
import { Button, Col, Form, InputNumber, Modal, Row, Tabs, Typography } from 'antd';
import {
  BENCHMARK_BAND_KEYS,
  BENCHMARK_BAND_LABELS,
  MetricRangeMap,
  MetricRuleSet,
  cleanBenchmarkBandRangeMap,
  SIMPLIFIED_RANGE_KEYS,
  collapseMetricRuleSetForSimplifiedEditing,
  collapseRangeMapForSimplifiedEditing,
  expandMetricRuleSetForSimplifiedStorage,
  formatRangeLabel,
} from '../../helpers/metricRules';

const { Text } = Typography;

interface Props {
  open: boolean;
  metric: any | null;
  initialRuleSet?: MetricRuleSet | null;
  defaults?: MetricRuleSet | null;
  saving?: boolean;
  onCancel: () => void;
  onSave: (nextRuleSet: MetricRuleSet | null) => void;
}

type RangeGroupKey = 'warningRange' | 'colorRange' | 'notificationRange' | 'healthStatusRange';

const RANGE_GROUPS: Array<{ key: RangeGroupKey; label: string; help: string }> = [
  {
    key: 'warningRange',
    label: 'Warning Ranges',
    help: 'Used for KPI/Table/Warning report highlights.',
  },
  {
    key: 'colorRange',
    label: 'Color Ranges',
    help: 'Used by visual components when using area default color mode.',
  },
  {
    key: 'notificationRange',
    label: 'Notification Ranges',
    help: 'Used by notification generation logic.',
  },
];

const SIMPLIFIED_RANGE_ROWS: Array<{
  key: (typeof SIMPLIFIED_RANGE_KEYS)[number];
  label: string;
  appliesToLabel: string;
}> = [
  {
    key: 'value',
    label: 'Value',
    appliesToLabel: 'Used for value, avg, average, min, max, sum, median',
  },
  {
    key: 'count',
    label: 'Count',
    appliesToLabel: 'Used for count only',
  },
  {
    key: 'variance',
    label: 'Variance / Std Dev',
    appliesToLabel: 'Shared for variance and stddev',
  },
];

const buildInitialRangeValues = (rangeMap?: MetricRangeMap) => {
  const simplified = collapseRangeMapForSimplifiedEditing(rangeMap);
  const result: Record<string, { min: number | null; max: number | null }> = {};
  SIMPLIFIED_RANGE_KEYS.forEach((key) => {
    const range = simplified?.[key];
    result[key] = {
      min: typeof range?.min === 'number' ? range.min : null,
      max: typeof range?.max === 'number' ? range.max : null,
    };
  });
  return result;
};

const buildInitialBenchmarkBandValues = (rangeMap?: MetricRangeMap) => {
  const cleaned = cleanBenchmarkBandRangeMap(rangeMap);
  const result: Record<string, { min: number | null; max: number | null }> = {};
  BENCHMARK_BAND_KEYS.forEach((key) => {
    const range = cleaned?.[key];
    result[key] = {
      min: typeof range?.min === 'number' ? range.min : null,
      max: typeof range?.max === 'number' ? range.max : null,
    };
  });
  return result;
};

const buildInitialValues = (ruleSet?: MetricRuleSet | null) => {
  const simplified = collapseMetricRuleSetForSimplifiedEditing(ruleSet);
  return {
    warningRange: buildInitialRangeValues(simplified?.warningRange),
    colorRange: buildInitialRangeValues(simplified?.colorRange),
    notificationRange: buildInitialRangeValues(simplified?.notificationRange),
    healthStatusRange: buildInitialRangeValues(simplified?.healthStatusRange),
    benchmarkRange: buildInitialBenchmarkBandValues(simplified?.benchmarkRange),
    notificationLogic: {
      minViolatingAggregations:
        typeof simplified?.notificationLogic?.minViolatingAggregations === 'number'
          ? simplified?.notificationLogic?.minViolatingAggregations
          : null,
    },
    healthStatusLogic: {
      attentionMinOutOfRangeCount:
        typeof simplified?.healthStatusLogic?.attentionMinOutOfRangeCount === 'number'
          ? simplified?.healthStatusLogic?.attentionMinOutOfRangeCount
          : null,
      actionNeededMinOutOfRangePercent:
        typeof simplified?.healthStatusLogic?.actionNeededMinOutOfRangePercent === 'number'
          ? simplified?.healthStatusLogic?.actionNeededMinOutOfRangePercent
          : null,
    },
  };
};

const MetricRangeEditor: React.FC<{
  groupKey: RangeGroupKey;
  defaults?: MetricRangeMap;
}> = ({ groupKey, defaults }) => {
  const simplifiedDefaults = collapseRangeMapForSimplifiedEditing(defaults);

  return (
    <div style={{ marginTop: 8 }}>
      {SIMPLIFIED_RANGE_ROWS.map((row) => (
        <Row gutter={12} key={`${groupKey}-${row.key}`} style={{ marginBottom: 8 }}>
          <Col xs={24} md={6}>
            <Text strong>{row.label}</Text>
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Default: {formatRangeLabel(simplifiedDefaults?.[row.key])}
              </Text>
            </div>
            <div>
              <Text type="secondary" style={{ fontSize: 11 }}>
                {row.appliesToLabel}
              </Text>
            </div>
          </Col>
          <Col xs={12} md={9}>
            <Form.Item name={[groupKey, row.key, 'min']} label="Min" style={{ marginBottom: 0 }}>
              <InputNumber style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={12} md={9}>
            <Form.Item name={[groupKey, row.key, 'max']} label="Max" style={{ marginBottom: 0 }}>
              <InputNumber style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>
      ))}
    </div>
  );
};

const BenchmarkBandEditor: React.FC<{
  defaults?: MetricRangeMap;
}> = ({ defaults }) => {
  const cleanedDefaults = cleanBenchmarkBandRangeMap(defaults);

  return (
    <div style={{ marginTop: 8 }}>
      {BENCHMARK_BAND_KEYS.map((bandKey) => (
        <Row gutter={12} key={`benchmark-${bandKey}`} style={{ marginBottom: 8 }}>
          <Col xs={24} md={6}>
            <Text strong>
              Band {bandKey} - {BENCHMARK_BAND_LABELS[bandKey]}
            </Text>
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Default: {formatRangeLabel(cleanedDefaults?.[bandKey])}
              </Text>
            </div>
          </Col>
          <Col xs={12} md={9}>
            <Form.Item name={['benchmarkRange', bandKey, 'min']} label="Min" style={{ marginBottom: 0 }}>
              <InputNumber style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={12} md={9}>
            <Form.Item name={['benchmarkRange', bandKey, 'max']} label="Max" style={{ marginBottom: 0 }}>
              <InputNumber style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>
      ))}
    </div>
  );
};

const MetricRuleEditorModal: React.FC<Props> = ({
  open,
  metric,
  initialRuleSet,
  defaults,
  saving,
  onCancel,
  onSave,
}) => {
  const [form] = Form.useForm();

  useEffect(() => {
    if (!open) return;
    form.setFieldsValue(buildInitialValues(initialRuleSet));
  }, [open, metric, initialRuleSet, form]);

  const handleSave = async () => {
    await form.validateFields();
    const values = form.getFieldsValue(true);
    const expanded = expandMetricRuleSetForSimplifiedStorage(values);
    onSave(expanded || null);
  };

  const clearAll = () => {
    form.setFieldsValue(buildInitialValues(null));
  };

  return (
    <Modal
      title={metric ? `Configure metric rules: ${metric.name || metric.key}` : 'Configure metric rules'}
      open={open}
      onCancel={onCancel}
      onOk={handleSave}
      confirmLoading={saving}
      okText="Save"
      width={860}
      footer={[
        <Button key="clear" onClick={clearAll} disabled={!metric}>
          Clear overrides
        </Button>,
        <Button key="cancel" onClick={onCancel}>
          Cancel
        </Button>,
        <Button key="save" type="primary" loading={saving} onClick={handleSave}>
          Save
        </Button>,
      ]}
    >
      {metric ? (
        <Form form={form} layout="vertical">
          <Text type="secondary">
            Configure simplified ranges per metric. Each edited value is expanded internally for all related aggregations.
          </Text>
          <Tabs
            style={{ marginTop: 12 }}
            items={[
              {
                key: 'benchmarkRange',
                label: 'Benchmark Bands',
                forceRender: true,
                children: (
                  <>
                    <Text type="secondary">
                      Define ideal benchmark bands from 1 to 5. Labels are fixed: 1=Poor, 2=Fair, 3=Good, 4=Great,
                      5=Perfect.
                    </Text>
                    <div>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Use ascending ranges so values can be consistently mapped to a benchmark label.
                      </Text>
                    </div>
                    <BenchmarkBandEditor defaults={defaults?.benchmarkRange} />
                  </>
                ),
              },
              ...RANGE_GROUPS.map((group) => ({
                key: group.key,
                label: group.label,
                forceRender: true,
                children: (
                  <>
                    <Text type="secondary">{group.help}</Text>
                    <MetricRangeEditor groupKey={group.key} defaults={defaults?.[group.key]} />
                  </>
                ),
              })),
              {
                key: 'notificationLogic',
                label: 'Notification Logic',
                forceRender: true,
                children: (
                  <Row gutter={12} style={{ marginTop: 8 }}>
                    <Col xs={24} md={12}>
                      <Form.Item
                        name={['notificationLogic', 'minViolatingAggregations']}
                        label="Minimum violating aggregations"
                        tooltip="Minimum number of out-of-range aggregations required before generating notification rows."
                      >
                        <InputNumber min={1} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                  </Row>
                ),
              },
              {
                key: 'healthStatus',
                label: 'Health Status',
                forceRender: true,
                children: (
                  <>
                    <Text type="secondary">
                      Health status is calculated per metric by comparing values against the ranges below, then applying
                      the thresholds in the same tab.
                    </Text>
                    <div style={{ marginTop: 8 }}>
                      <Text strong>How it works</Text>
                      <div>
                        <Text type="secondary">
                          1. A reading is out-of-range when it is lower than Min or higher than Max.
                        </Text>
                      </div>
                      <div>
                        <Text type="secondary">
                          2. Out-of-range % = out-of-range count / total readings.
                        </Text>
                      </div>
                      <div>
                        <Text type="secondary">
                          3. Action Needed if out-of-range % is strictly greater than the Action Needed threshold.
                        </Text>
                      </div>
                      <div>
                        <Text type="secondary">
                          4. Otherwise Attention if out-of-range count is greater than or equal to the Attention count.
                        </Text>
                      </div>
                      <div>
                        <Text type="secondary">5. Otherwise status is Healthy.</Text>
                      </div>
                    </div>

                    <div style={{ marginTop: 10 }}>
                      <Text strong>Example</Text>
                      <div>
                        <Text type="secondary">
                          Ranges: Min 0.40 / Max 0.85, Attention count = 2, Action Needed % = 0.20.
                        </Text>
                      </div>
                      <div>
                        <Text type="secondary">
                          Case A: 20 readings, 3 out-of-range -&gt; 3/20 = 0.15 (15%). 0.15 is not above 0.20, but 3 is
                          at least 2, so status = Attention.
                        </Text>
                      </div>
                      <div>
                        <Text type="secondary">
                          Case B: 20 readings, 5 out-of-range -&gt; 5/20 = 0.25 (25%). 0.25 is above 0.20, so status =
                          Action Needed.
                        </Text>
                      </div>
                      <div>
                        <Text type="secondary">
                          Case C: 20 readings, 1 out-of-range -&gt; 1/20 = 0.05 (5%). Not above 0.20 and count is below
                          2, so status = Healthy.
                        </Text>
                      </div>
                    </div>

                    <div style={{ marginTop: 12 }}>
                      <Text strong>Health Status Ranges</Text>
                      <div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          Define Min/Max limits used to mark each reading as in-range or out-of-range.
                        </Text>
                      </div>
                      <MetricRangeEditor groupKey="healthStatusRange" defaults={defaults?.healthStatusRange} />
                    </div>

                    <div style={{ marginTop: 12 }}>
                      <Text strong>Health Status Logic</Text>
                      <Row gutter={12} style={{ marginTop: 8 }}>
                        <Col xs={24} md={12}>
                          <Form.Item
                            name={['healthStatusLogic', 'attentionMinOutOfRangeCount']}
                            label="Attention min out-of-range count"
                            tooltip="If Action Needed is not triggered, this count threshold sets status to Attention."
                          >
                            <InputNumber min={1} style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                          <Form.Item
                            name={['healthStatusLogic', 'actionNeededMinOutOfRangePercent']}
                            label="Action needed min out-of-range percent"
                            tooltip="Value between 0 and 1. Example: 0.10 means more than 10% out-of-range becomes Action Needed."
                          >
                            <InputNumber min={0} max={1} step={0.01} style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                      </Row>
                    </div>
                  </>
                ),
              },
            ]}
          />
        </Form>
      ) : (
        <Text type="secondary">Select a metric to edit.</Text>
      )}
    </Modal>
  );
};

export default MetricRuleEditorModal;
